import { useState, useEffect } from "react";
import Icon from "./Icon";

const CATEGORIES = [
  { label: "☕ Coffee", type: "cafe" },
  { label: "🍽 Restaurants", type: "restaurant" },
  { label: "🛒 Grocery", type: "supermarket" },
  { label: "💊 Pharmacy", type: "pharmacy" },
  { label: "🏋 Gym", type: "gym" },
  { label: "🍺 Bar", type: "bar" },
];

// Geocode an address to get lat/lng using Google Places text search
async function geocodeAddress(address) {
  try {
    const res = await fetch("/api/places", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ query: address }),
    });
    const data = await res.json();
    const first = data.results?.[0];
    if (first?.lat && first?.lng) return { lat: first.lat, lng: first.lng };
  } catch {}
  return null;
}

export default function NearbyModal({ segment, onClose, onAddSegment }) {
  const [activeCategory, setActiveCategory] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hotelCoords, setHotelCoords] = useState(
    segment.location ? { lat: segment.location.lat, lng: segment.location.lng } : null
  );
  const [locatingGPS, setLocatingGPS] = useState(false);

  // Geocode hotel address on mount if no coordinates stored
  useEffect(() => {
    if (!hotelCoords && segment.address) {
      geocodeAddress(segment.address).then(coords => {
        if (coords) {
          setHotelCoords(coords);
        } else {
          // Geocoding failed — try device GPS as fallback
          tryGPS();
        }
      });
    } else if (!hotelCoords && !segment.address) {
      tryGPS();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function tryGPS() {
    if (!navigator.geolocation) return;
    setLocatingGPS(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setHotelCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocatingGPS(false);
      },
      () => { setLocatingGPS(false); },
      { timeout: 8000, maximumAge: 60000 }
    );
  }

  const handleCategory = async (cat) => {
    setActiveCategory(cat);
    setLoading(true);
    setResults([]);
    setError(null);

    try {
      let body;
      if (hotelCoords) {
        // Use proper Nearby Search with coordinates — sorted by distance
        body = { lat: hotelCoords.lat, lng: hotelCoords.lng, type: cat.type };
      } else {
        // Fall back to text search
        body = { query: `${cat.label.replace(/^.\s/, "")} near ${segment.address || segment.title}` };
      }

      const res = await fetch("/api/places", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setResults(data.results || []);
    } catch (e) {
      setError("Search failed — please try again");
    }
    setLoading(false);
  };

  const handleAdd = (place) => {
    const type = activeCategory?.label?.includes("Restaurant") ||
                 activeCategory?.label?.includes("Coffee") ||
                 activeCategory?.label?.includes("Bar") ? "restaurant" : "activity";
    onAddSegment({
      type,
      title: place.name || "",
      address: place.address || "",
      location: place.lat && place.lng ? { lat: place.lat, lng: place.lng } : null,
      date: segment.date || "",
      time: "",
      detail: place.rating ? `Rating: ${place.rating}⭐` : "",
      confirmation: "",
      link: place.website || "",
      phone: place.phone || "",
      checkoutDate: "",
    });
    onClose();
  };

  const handleShowOnMap = (place) => {
    window.open(`https://www.google.com/maps?q=${place.lat},${place.lng}`, "_blank");
  };

  // Open Google Maps showing the hotel location so user can browse nearby
  const handleOpenInMaps = () => {
    if (!hotelCoords) return;
    const cat = activeCategory?.label?.replace(/^.\s/, "") || "nearby places";
    window.open(
      `https://www.google.com/maps/search/${encodeURIComponent(cat)}/@${hotelCoords.lat},${hotelCoords.lng},16z`,
      "_blank"
    );
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <div>
            <div className="modal-title">Nearby</div>
            <div style={{ fontSize: 12, color: "var(--ink-light)" }}>
              Near {segment.title}
              {!hotelCoords && <span style={{ color: "#C05621" }}> · {locatingGPS ? "using GPS..." : "locating..."}</span>}
            </div>
          </div>
          <button className="modal-close" onClick={onClose}><Icon name="close" size={14} /></button>
        </div>

        {/* Category buttons */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
          {CATEGORIES.map((cat) => (
            <button
              key={cat.label}
              className={`type-btn ${activeCategory?.label === cat.label ? "selected" : ""}`}
              onClick={() => handleCategory(cat)}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {loading && (
          <div style={{ textAlign: "center", padding: "16px 0", color: "var(--ink-light)", fontSize: 13 }}>
            Searching nearby...
          </div>
        )}
        {error && (
          <div style={{ textAlign: "center", padding: "12px 0", color: "#C05621", fontSize: 13 }}>
            {error}
          </div>
        )}
        {!loading && !error && results.length === 0 && activeCategory && (
          <div style={{ textAlign: "center", padding: "16px 0", color: "var(--ink-light)", fontSize: 13 }}>
            No results found nearby
          </div>
        )}

        {results.length > 0 && (
          <>
            {hotelCoords && (
              <button
                onClick={handleOpenInMaps}
                className="seg-btn"
                style={{ display: "flex", justifyContent: "center", width: "100%", marginBottom: 12 }}
              >
                <Icon name="map" size={12} /> Browse on Google Maps
              </button>
            )}

            {results.map((place, i) => (
              <div key={i} style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "10px 0", borderBottom: "1px solid var(--border)"
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <div style={{ fontSize: 14, fontWeight: 500, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {place.name || "Unknown"}
                    </div>
                    {place.rating && (
                      <div style={{ fontSize: 11, color: "var(--ink-light)", flexShrink: 0 }}>
                        ⭐ {place.rating}
                      </div>
                    )}
                    {place.distance != null && (
                      <div style={{ fontSize: 11, color: "var(--ink-faint)", flexShrink: 0 }}>
                        {place.distance < 0.1 ? `${Math.round(place.distance * 5280)} ft` : `${place.distance} mi`}
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--ink-light)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {place.address || "Address unavailable"}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  {place.lat && place.lng && (
                    <button
                      onClick={() => handleShowOnMap(place)}
                      className="seg-btn"
                      style={{ padding: "5px 10px" }}
                    >
                      <Icon name="map" size={11} />
                    </button>
                  )}
                  <button
                    className="seg-btn primary"
                    style={{ padding: "5px 10px", fontSize: 11 }}
                    onClick={() => handleAdd(place)}
                  >
                    + Add
                  </button>
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
